import {InvalidTokenError} from '@packages/utils';
import {parseCookies} from 'nookies';
import {cookieApiRefreshTokenKey} from '../../../server/token';
import type {ResultLogin} from '../../repositories/loginRepository';
import {refreshAccesstoken} from '../../repositories/sessionRepository';
import {sendOauthTokenToRNApp} from './sendOauthTokenToRNApp';
import {getSessionEpoch, isSessionEpochCurrent} from './sessionEpoch';
import {
  setAccessTokenToCookie,
  setRefreshTokenToCookie,
} from './setOauthTokenToCookie';

const inFlightRefreshByVersion = new Map<
  string | undefined,
  Promise<ResultLogin>
>();

const invalidToken: ResultLogin = {
  ok: false,
  error: InvalidTokenError,
};

// ログアウトはエポックを進めCookieを削除するが、どちらか一方しか観測できない
// タイミングがありうるため両方を確認する
const isSessionAlive = (epochAtStart: number): boolean =>
  isSessionEpochCurrent(epochAtStart) &&
  Boolean(parseCookies()[cookieApiRefreshTokenKey]);

const doRefresh = async (version?: string): Promise<ResultLogin> => {
  const epochAtStart = getSessionEpoch();

  // 呼び出し元から渡るcookiesはスナップショットであり、
  // 別タブのリフレッシュで既にローテーション済みの場合があるため直前に再読する
  const latestRefreshToken = parseCookies()[cookieApiRefreshTokenKey];
  if (!latestRefreshToken) {
    return invalidToken;
  }

  const result = await refreshAccesstoken({
    refreshToken: latestRefreshToken,
    version,
  });
  if (!result.ok) {
    return result;
  }

  // リフレッシュ中にログアウトした場合、ここでトークンを書き戻すと
  // 終了したはずのセッションが復活してしまう
  if (!isSessionAlive(epochAtStart)) {
    return invalidToken;
  }

  const {accessToken: newAccessToken, refreshToken: newRefreshToken} =
    result.value;

  setAccessTokenToCookie({accessToken: newAccessToken});
  setRefreshTokenToCookie({refreshToken: newRefreshToken});

  if ((window as any).ReactNativeWebView) {
    sendOauthTokenToRNApp({
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
    });
  }

  return result;
};

export const refreshAccessTokenWithLock = async ({
  refreshToken,
  version,
}: {
  refreshToken?: string;
  version?: string;
}): Promise<ResultLogin> => {
  // サーバーサイドではモジュールスコープがリクエスト間(=ユーザー間)で共有されるため、
  // 排他制御を行うと別ユーザーのトークンを返してしまう
  if (typeof window === 'undefined') {
    return refreshAccesstoken({refreshToken, version});
  }

  // versionが異なるリクエストは別のエンドポイントを叩くため実行中のものを共有できない。
  // 単一のスロットで持つと実行中のものを取り違えるためversionごとに保持する
  const inFlight = inFlightRefreshByVersion.get(version);
  if (inFlight) {
    return inFlight;
  }

  const refresh: Promise<ResultLogin> = doRefresh(version).finally(() => {
    // 破棄や再ログインで既に別のリフレッシュが登録されている場合、
    // ここで削除すると新しい方の排他制御が壊れる
    if (inFlightRefreshByVersion.get(version) === refresh) {
      inFlightRefreshByVersion.delete(version);
    }
  });
  inFlightRefreshByVersion.set(version, refresh);

  return refresh;
};

// 実行中のリフレッシュはログアウト前のセッションのものなので、
// 再ログイン後の呼び出しが結果を共有すると失効扱いになってしまう
export const discardInFlightRefresh = (): void => {
  inFlightRefreshByVersion.clear();
};

export const __resetInFlightRefreshForTest = discardInFlightRefresh;
